import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  where,
  Timestamp,
} from "firebase/firestore";
import { db } from "../../firebase/config";
import { useProfile } from "../../contexts/ProfileContext";
import { useExperiences } from "../../contexts/ExperienceContext";
import { useEducation } from "../../contexts/EducationContext";
import { usePublications } from "../../contexts/PublicationContext";
import {
  UserIcon,
  DocumentTextIcon,
  AcademicCapIcon,
  BriefcaseIcon,
  NewspaperIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  CommandLineIcon,
  EnvelopeIcon,
  WrenchScrewdriverIcon,
  LightBulbIcon,
  DocumentDuplicateIcon,
} from "@heroicons/react/24/outline";

// Dashboard Card Component
const DashboardCard = ({
  title,
  value,
  icon,
  link,
  subtitle,
}: {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  link: string;
  subtitle: string;
}) => {
  return (
    <Link to={link} className="block">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow">
        <div className="flex items-start justify-between">
          <div>
            <h4 className="text-gray-700 dark:text-gray-300 font-medium">
              {title}
            </h4>
            <p className="text-2xl font-bold mt-1 text-gray-900 dark:text-white">
              {value}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {subtitle}
            </p>
          </div>
          <div>{icon}</div>
        </div>
      </div>
    </Link>
  );
};

interface StatsCardProps {
  title: string;
  value: number | string;
  description: string;
  icon: React.ReactNode;
  color: string;
  linkTo: string;
}

const StatsCard: React.FC<StatsCardProps> = ({
  title,
  value,
  description,
  icon,
  color,
  linkTo,
}) => {
  return (
    <Link
      to={linkTo}
      className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
    >
      <div className="flex items-start">
        <div className={`p-3 rounded-full ${color} text-white mr-4`}>
          {icon}
        </div>
        <div>
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
            {title}
          </p>
          <h3 className="text-2xl font-bold mt-1 mb-1">{value}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {description}
          </p>
        </div>
      </div>
    </Link>
  );
};

const AdminDashboard: React.FC = () => {
  const { profile } = useProfile();
  const { experiences } = useExperiences();
  const { education } = useEducation();
  const { publications } = usePublications();

  const [stats, setStats] = useState({
    totalProjects: 0,
    totalMessages: 0,
    unreadMessages: 0,
    lastUpdated: "",
  });
  const [loading, setLoading] = useState(true);
  const [recentMessages, setRecentMessages] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [messages, setMessages] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Get project count
        const projectsQuery = query(collection(db, "projects"));
        const projectsSnapshot = await getDocs(projectsQuery);
        const projectsData = projectsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setProjects(projectsData);
        const projectCount = projectsSnapshot.size;

        // Get message stats
        const messagesQuery = query(collection(db, "messages"));
        const messagesSnapshot = await getDocs(messagesQuery);
        const messagesData = messagesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setMessages(messagesData);
        const messageCount = messagesSnapshot.size;

        // Get unread message count
        const unreadQuery = query(
          collection(db, "messages"),
          where("read", "==", false)
        );
        const unreadSnapshot = await getDocs(unreadQuery);
        const unreadMessageCount = unreadSnapshot.size;
        setUnreadCount(unreadMessageCount);

        // Get recent messages
        const recentMessagesQuery = query(
          collection(db, "messages"),
          orderBy("createdAt", "desc"),
          limit(5)
        );
        const recentMessagesSnapshot = await getDocs(recentMessagesQuery);
        const recentMessagesData = recentMessagesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setStats({
          totalProjects: projectCount,
          totalMessages: messageCount,
          unreadMessages: unreadMessageCount,
          lastUpdated: new Date().toLocaleString(),
        });
        setRecentMessages(recentMessagesData);
      } catch (error) {
        console.error("Error fetching dashboard stats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  // Format date
  const formatDate = (timestamp: any) => {
    if (!timestamp) return "N/A";

    if (timestamp instanceof Timestamp) {
      return timestamp.toDate().toLocaleString();
    }

    return new Date(timestamp).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Last updated: {stats.lastUpdated}
        </p>
      </div>

      <div className="space-y-4">
        <h3 className="text-xl font-semibold">Content Status</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <DashboardCard
            title="Profile"
            value={profile ? "Complete" : "Incomplete"}
            icon={<UserIcon className="h-8 w-8 text-blue-500" />}
            link="/admin/profile"
            subtitle={`Last updated: ${
              profile?.lastUpdated
                ? new Date(profile.lastUpdated.toDate()).toLocaleDateString()
                : "Never"
            }`}
          />
          <DashboardCard
            title="Experience"
            value={`${experiences.length} entries`}
            icon={<BriefcaseIcon className="h-8 w-8 text-emerald-500" />}
            link="/admin/experience"
            subtitle="Work history and achievements"
          />
          <DashboardCard
            title="Education"
            value={`${education.length} entries`}
            icon={<AcademicCapIcon className="h-8 w-8 text-purple-500" />}
            link="/admin/education"
            subtitle="Academic background"
          />
          <DashboardCard
            title="Projects"
            value={`${projects.length} entries`}
            icon={<CommandLineIcon className="h-8 w-8 text-amber-500" />}
            link="/admin/projects"
            subtitle="Portfolio work showcase"
          />
          <DashboardCard
            title="Publications"
            value={`${publications.length} entries`}
            icon={<DocumentTextIcon className="h-8 w-8 text-teal-500" />}
            link="/admin/publications"
            subtitle="Research and articles"
          />
          <DashboardCard
            title="Contact Messages"
            value={`${messages.length} messages`}
            icon={<EnvelopeIcon className="h-8 w-8 text-rose-500" />}
            link="/admin/messages"
            subtitle={`${unreadCount} unread`}
          />
          <DashboardCard
            title="Skills"
            value={`${
              profile?.skills?.reduce(
                (acc: number, skill: any) => acc + (skill?.items?.length || 0),
                0
              ) || 0
            } skills`}
            icon={<WrenchScrewdriverIcon className="h-8 w-8 text-orange-500" />}
            link="/admin/profile"
            subtitle="Technical competencies"
          />
          <DashboardCard
            title="Approach"
            value={`${profile?.approachItems?.length || 0} items`}
            icon={<LightBulbIcon className="h-8 w-8 text-yellow-500" />}
            link="/admin/profile"
            subtitle="Professional philosophy"
          />
          <DashboardCard
            title="Resume Summary"
            value={profile?.resumeSummary ? "Complete" : "Incomplete"}
            icon={<DocumentDuplicateIcon className="h-8 w-8 text-indigo-500" />}
            link="/admin/profile"
            subtitle="Career overview"
          />
          <DashboardCard
            title="Projects Description"
            value={profile?.projectsDescription ? "Complete" : "Incomplete"}
            icon={<DocumentTextIcon className="h-8 w-8 text-green-500" />}
            link="/admin/profile"
            subtitle="Project section intro"
          />
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
        <h2 className="text-xl font-semibold mb-4">Recent Messages</h2>
        {recentMessages.length > 0 ? (
          <div className="space-y-4">
            {recentMessages.map((message: any) => (
              <div
                key={message.id}
                className={`p-4 border rounded-lg ${
                  !message.read
                    ? "border-primary-500 dark:border-primary-400 bg-primary-50 dark:bg-primary-900/10"
                    : "border-gray-200 dark:border-gray-700"
                }`}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-medium">{message.name}</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {message.email}
                    </p>
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {formatDate(message.createdAt)}
                  </span>
                </div>
                <p className="mt-2 text-sm font-medium">{message.subject}</p>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                  {message.message}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 dark:text-gray-400">No messages yet!</p>
        )}

        {recentMessages.length > 0 && (
          <div className="mt-4 text-right">
            <Link
              to="/admin/messages"
              className="text-primary-600 dark:text-primary-400 text-sm font-medium hover:underline"
            >
              View all messages
            </Link>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Link
          to="/admin/experience"
          className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow flex items-center"
        >
          <BriefcaseIcon className="h-8 w-8 text-amber-500 mr-4" />
          <div>
            <h3 className="font-semibold">Experience</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Manage work experience
            </p>
          </div>
        </Link>

        <Link
          to="/admin/education"
          className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow flex items-center"
        >
          <AcademicCapIcon className="h-8 w-8 text-red-500 mr-4" />
          <div>
            <h3 className="font-semibold">Education</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Manage education details
            </p>
          </div>
        </Link>

        <Link
          to="/admin/publications"
          className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow flex items-center"
        >
          <NewspaperIcon className="h-8 w-8 text-emerald-500 mr-4" />
          <div>
            <h3 className="font-semibold">Publications</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Manage publications
            </p>
          </div>
        </Link>
      </div>
    </div>
  );
};

export default AdminDashboard;
